export interface RequestDisplayData {
  insuranceCompanyName: string;
  policyNumber: string;
  vinNumber: string;
  engineNumber: string;
  servicePackageName: string;
  policyStartDate: string;
  policyExpiryDate: string;
  isPolicyExpired: boolean;
  serviceType: string;
  pickupLocation: string;
  destinationLocation: string;
  clientId?: number;
  hasInsuranceData: boolean;
}

const unwrapEntity = (value: any): any => {
  if (!value) return null;
  if (value.data?.attributes) return { ...value.data.attributes, id: value.data.id };
  if (value.data) return value.data;
  if (value.attributes) return value.attributes;
  return value;
};

const resolveRelationName = (relation: any, keys: string[] = ['name', 'code']): string => {
  const data = unwrapEntity(relation);
  if (!data) return '';
  for (const key of keys) {
    if (data[key]) return String(data[key]);
  }
  return '';
};

export const formatRequestDate = (value: string | null | undefined): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleDateString();
};

const formatCoordinates = (lat?: number | string | null, lng?: number | string | null): string => {
  if (lat == null || lng == null || lat === '' || lng === '') return '';
  return `${lat}, ${lng}`;
};

export const getRequestDisplayData = (request: any): RequestDisplayData => {
  const attrs = request?.attributes || request || {};
  const insuranceClient = unwrapEntity(attrs.insuranceClient);
  const insuranceCompany = unwrapEntity(insuranceClient?.insuranceCompany);
  const clientServicePackage = unwrapEntity(insuranceClient?.servicePackage);
  const requestServicePackage = unwrapEntity(attrs.servicePackage);

  const servicePackageName =
    resolveRelationName(clientServicePackage, ['name', 'code']) ||
    resolveRelationName(requestServicePackage, ['name', 'code']);

  const pickupLocation =
    attrs.locationText ||
    formatCoordinates(attrs.locationLatitude, attrs.locationLongitude) ||
    '-';

  const destinationLocation =
    attrs.toLocationText ||
    attrs.toDropDown ||
    formatCoordinates(attrs.toLocationLatitude, attrs.toLocationLongitude) ||
    '-';

  const serviceType =
    attrs.serviceType ||
    attrs.serviceCode ||
    '-';

  const clientId =
    attrs.insuranceClient?.data?.id ||
    attrs.insuranceClient?.id ||
    insuranceClient?.id;

  const insuranceCompanyName = insuranceCompany?.name || '-';
  const policyNumber = insuranceClient?.policyNumber || '-';
  const vinNumber = insuranceClient?.vinNumber || attrs.vinNumber || '-';
  const engineNumber = insuranceClient?.engineNumber || '-';
  const policyStartDate = formatRequestDate(insuranceClient?.policyStartDate);
  const policyExpiryDate = formatRequestDate(insuranceClient?.policyEndDate);
  const isPolicyExpired = Boolean(
    insuranceClient?.policyEndDate &&
    !isNaN(new Date(insuranceClient.policyEndDate).getTime()) &&
    new Date(insuranceClient.policyEndDate) < new Date()
  );

  const hasInsuranceData =
    insuranceCompanyName !== '-' ||
    policyNumber !== '-' ||
    vinNumber !== '-' ||
    engineNumber !== '-' ||
    servicePackageName !== '' ||
    policyStartDate !== '-' ||
    policyExpiryDate !== '-';

  return {
    insuranceCompanyName,
    policyNumber,
    vinNumber,
    engineNumber,
    servicePackageName: servicePackageName || '-',
    policyStartDate,
    policyExpiryDate,
    isPolicyExpired,
    serviceType,
    pickupLocation,
    destinationLocation,
    clientId: clientId ? Number(clientId) : undefined,
    hasInsuranceData,
  };
};
